<template>
  <el-dialog
    v-dialogDrag
    v-dialogResize
    custom-class="measurement-form-dialog"
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="70%"
    :close-on-click-modal="false"
    append-to-body
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="measurement-wrapper" v-loading="loading">
      <!-- 打印区域 -->
      <div class="print-area" ref="printArea">
        <!-- 表单头部 -->
        <div class="form-header" v-if="hasData">
          <div class="form-header-left">
            <span class="header-label">项目名称：</span>
            <span class="header-value">{{ formData.projectName || '' }}</span>
          </div>
          <div class="form-header-right">
            <span class="header-label">收方日期：</span>
            <span class="header-value">{{ formData.checkDate || '' }}</span>
          </div>
        </div>

        <!-- 主表格 -->
        <table class="mf-table" v-if="hasData">
          <thead>
            <tr>
              <th rowspan="2" class="col-stage">工序</th>
              <th rowspan="2" class="col-part">部位</th>
              <th colspan="3" class="col-group">立面面积</th>
              <th colspan="2" class="col-group">平面面积</th>
              <th rowspan="2" class="col-worker">施工人员姓名</th>
            </tr>
            <tr>
              <th class="col-perimeter">周长L(m)</th>
              <th class="col-height">高度H(m)</th>
              <th class="col-area">面积S(m²)</th>
              <th class="col-expr">计算式</th>
              <th class="col-plane">面积S(m²)</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(stage, sIdx) in stages">
              <tr v-for="(row, rIdx) in getStageRows(stage)" :key="'s' + sIdx + '-' + row._type + rIdx">
                <td v-if="row._first && row._type === 'item'" :rowspan="stageRowSpan(stage)" class="td-stage">{{ stage.stageName }}</td>
                <template v-if="row._type === 'item'">
                  <td class="td-part">{{ row.partName || '' }}</td>
                  <td class="td-num">{{ formatNum(row.perimeter) }}</td>
                  <td class="td-num">{{ formatNum(row.height) }}</td>
                  <td class="td-num">{{ formatNum(row.area) }}</td>
                  <td class="td-expr">{{ row.planeExpressions || '' }}</td>
                  <td class="td-num">{{ formatNum(row.planeArea) }}</td>
                </template>
                <td v-if="row._type === 'count'" :colspan="7" class="td-count-row">{{ row._label }}</td>
                <td v-if="row._first && row._type === 'item'" :rowspan="stageRowSpan(stage)" class="td-worker">{{ stage.workerName || '' }}</td>
              </tr>
            </template>

            <!-- 增项行 -->
            <template v-if="extraMapEntries.length">
              <tr v-for="entry in extraMapEntries" :key="'extra-' + entry.key">
                <td class="td-stage">增项{{ entry.key }}</td>
                <td :colspan="7" class="td-extra-desc">{{ entry.value }}</td>
              </tr>
            </template>

            <!-- 工程方量汇总行 -->
            <tr class="tr-summary">
              <td colspan="2" class="td-summary-label">工程方量（平方）</td>
              <td colspan="2" class="td-summary-cell">
                <span class="summary-tag">S立面面积：</span>{{ formatNum(formData.totalArea) }}
              </td>
              <td colspan="2" class="td-summary-cell">
                <span class="summary-tag">S平面面积：</span>{{ formatNum(formData.totalPlaneArea) }}
              </td>
              <td colspan="2" class="td-summary-cell td-summary-total">
                <span class="summary-tag">合计方量：</span>{{ formatNum(formData.totalSquareVolume) }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 签名区域 -->
        <div class="sign-section" v-if="formData.signInfo">
          <div class="sign-title">项目经理/业主签字：</div>
          <div class="sign-body">
            <div class="sign-image-area" v-if="formData.signInfo && formData.signInfo.signImageUrl">
              <img :src="formData.signInfo.signImageUrl" alt="签名" class="sign-photo" />
            </div>
            <div class="sign-info-area">
              <div class="sign-info-row" v-if="formData.signInfo && formData.signInfo.signerName">
                <span class="sign-info-label">签名人：</span>{{ formData.signInfo.signerName }}
              </div>
              <div class="sign-info-row" v-if="formData.signInfo && formData.signInfo.signTime">
                <span class="sign-info-label">日期：</span>{{ formatDate(formData.signInfo.signTime) }}
              </div>
            </div>
          </div>
        </div>
        <div class="sign-section" v-else>
          <div class="sign-title">项目经理/业主签字：</div>
          <div class="sign-line"></div>
        </div>

        <!-- 说明区域 -->
        <div class="notes-section" v-if="hasData">
          <div class="notes-title">说明：</div>
          <div class="notes-item">1. 基坑回填面积不足4平方，按照4平方核算；</div>
          <div class="notes-item">2. 单次防水施工面积（基坑防水+面层防水）不足25平方，按照25平方核算；</div>
          <div class="notes-indent">非我司原因导致需二次上门补刷，费用100元（2个平方以内），超出2个平方费用核算方式：100+（实际面积-2）*单价。</div>
        </div>
      </div>

      <!-- 无数据提示 -->
      <div v-if="!loading && !hasData" class="no-data-block">
        <i class="el-icon-document" style="font-size: 40px; color: #c0c4cc;"></i>
        <p>暂无收方表数据</p>
      </div>
    </div>

    <span slot="footer" class="dialog-footer no-print">
      <el-button type="primary" size="small" @click="handlePrint" icon="el-icon-printer" v-if="hasData">打印</el-button>
      <el-button type="danger" size="small" @click="handleClose" icon="el-icon-circle-close-outline">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getSquareVolume } from "@/api/work/waterProof/merchants";

export default {
  name: "ViewMeasurementForm",
  directives: {
    dialogDrag: {
      bind(el) {
        const header = el.querySelector('.el-dialog__header');
        const dragDom = el.querySelector('.el-dialog');
        if (!header || !dragDom) return;
        const onMouseDown = (e) => {
          // 点击关闭按钮时不进入拖拽，避免拖拽导致关闭按钮"被带走"使 click 失效
          if (e.target.closest('.el-dialog__close')) return;
          if (e.target.closest('.el-dialog__headerbtn')) return;
          if (dragDom.style.position !== 'absolute') {
            const rect = dragDom.getBoundingClientRect();
            dragDom.style.position = 'absolute';
            dragDom.style.margin = '0';
            dragDom.style.left = rect.left + 'px';
            dragDom.style.top = rect.top + 'px';
          }
          const disX = e.clientX - header.offsetLeft;
          const disY = e.clientY - header.offsetTop;
          const dragDomWidth = dragDom.offsetWidth;
          const dragDomHeight = dragDom.offsetHeight;
          const screenWidth = document.documentElement.clientWidth;
          const screenHeight = document.documentElement.clientHeight;
          const minL = dragDom.offsetLeft;
          const maxL = screenWidth - dragDom.offsetLeft - dragDomWidth;
          const minT = dragDom.offsetTop;
          const maxT = screenHeight - dragDom.offsetTop - dragDomHeight;
          const styL = getComputedStyle(dragDom).left;
          const styT = getComputedStyle(dragDom).top;
          let left = styL.includes('%') ? +document.body.clientWidth * (+styL.replace(/%/g, '') / 100) : +styL.replace(/px/g, '');
          let top = styT.includes('%') ? +document.body.clientHeight * (+styT.replace(/%/g, '') / 100) : +styT.replace(/px/g, '');
          const onMouseMove = (ev) => {
            let ml = ev.clientX - disX, mt = ev.clientY - disY;
            if (-ml > minL) ml = -minL; else if (ml > maxL) ml = maxL;
            if (-mt > minT) mt = -minT; else if (mt > maxT) mt = maxT;
            dragDom.style.cssText += `;left:${ml + left}px;top:${mt + top}px;`;
          };
          const onMouseUp = () => {
            document.onmousemove = null;
            document.onmouseup = null;
          };
          document.onmousemove = onMouseMove;
          document.onmouseup = onMouseUp;
        };
        header._dialogDragMouseDown = onMouseDown;
        header.onmousedown = onMouseDown;
      },
      unbind(el) {
        const header = el.querySelector('.el-dialog__header');
        if (header && header._dialogDragMouseDown) {
          header.onmousedown = null;
          delete header._dialogDragMouseDown;
        }
        // 清理可能残留的全局事件，避免污染其他弹窗
        document.onmousemove = null;
        document.onmouseup = null;
      },
    },
    dialogResize: {
      bind(el) {
        const dragDom = el.querySelector('.el-dialog');
        const header = el.querySelector('.el-dialog__header');
        if (!dragDom) return;
        const EDGE = 8;
        const cursorMap = { top: 'ns-resize', bottom: 'ns-resize', left: 'ew-resize', right: 'ew-resize', 'top-left': 'nwse-resize', 'bottom-right': 'nwse-resize', 'top-right': 'nesw-resize', 'bottom-left': 'nesw-resize' };
        const getDir = (e) => {
          const r = dragDom.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          if (x < 0 || y < 0 || x > r.width || y > r.height) return '';
          const t = y < EDGE, b = y > r.height - EDGE, l = x < EDGE, ri = x > r.width - EDGE;
          if (t && l) return 'top-left'; if (t && ri) return 'top-right';
          if (b && l) return 'bottom-left'; if (b && ri) return 'bottom-right';
          if (t) return 'top'; if (b) return 'bottom'; if (l) return 'left'; if (ri) return 'right';
          return '';
        };
        const onMouseMove = (e) => {
          if (document._isDialogResizing) return;
          const d = getDir(e);
          if (d) { dragDom.style.cursor = cursorMap[d]; if (header) header.style.cursor = cursorMap[d]; }
          else if (header && header.contains(e.target)) { header.style.cursor = 'move'; dragDom.style.cursor = ''; }
          else { dragDom.style.cursor = ''; if (header) header.style.cursor = ''; }
        };
        const onMouseDown = (e) => {
          const d = getDir(e); if (!d) return;
          e.stopPropagation(); e.preventDefault();
          document._isDialogResizing = true;
          const rect = dragDom.getBoundingClientRect();
          if (dragDom.style.position !== 'absolute') { dragDom.style.position = 'absolute'; dragDom.style.margin = '0'; dragDom.style.left = rect.left + 'px'; dragDom.style.top = rect.top + 'px'; }
          const sx = e.clientX, sy = e.clientY, sw = rect.width, sh = rect.height, sl = rect.left, st = rect.top;
          const minW = 320, minH = 200, scW = document.documentElement.clientWidth, scH = document.documentElement.clientHeight;
          const onResizeMove = (ev) => {
            const dx = ev.clientX - sx, dy = ev.clientY - sy;
            let nw = sw, nh = sh, nl = sl, nt = st;
            if (d.indexOf('right') >= 0) nw = Math.max(minW, Math.min(sw + dx, scW - sl));
            if (d.indexOf('bottom') >= 0) nh = Math.max(minH, Math.min(sh + dy, scH - st));
            if (d.indexOf('left') >= 0) { nw = Math.max(minW, Math.min(sw - dx, sl + sw)); nl = sl + (sw - nw); }
            if (d.indexOf('top') >= 0) { nh = Math.max(minH, Math.min(sh - dy, st + sh)); nt = st + (sh - nh); }
            dragDom.style.width = nw + 'px'; dragDom.style.height = nh + 'px'; dragDom.style.left = nl + 'px'; dragDom.style.top = nt + 'px';
          };
          const onResizeUp = () => {
            document._isDialogResizing = false;
            document.onmousemove = null;
            document.onmouseup = null;
          };
          document.onmousemove = onResizeMove;
          document.onmouseup = onResizeUp;
        };
        dragDom._dialogResizeMouseMove = onMouseMove;
        dragDom._dialogResizeMouseDown = onMouseDown;
        dragDom.addEventListener('mousemove', onMouseMove);
        dragDom.addEventListener('mousedown', onMouseDown, true);
      },
      unbind(el) {
        const dragDom = el.querySelector('.el-dialog');
        if (dragDom) {
          if (dragDom._dialogResizeMouseMove) {
            dragDom.removeEventListener('mousemove', dragDom._dialogResizeMouseMove);
            delete dragDom._dialogResizeMouseMove;
          }
          if (dragDom._dialogResizeMouseDown) {
            dragDom.removeEventListener('mousedown', dragDom._dialogResizeMouseDown, true);
            delete dragDom._dialogResizeMouseDown;
          }
        }
        // 清理可能残留的全局事件和状态
        document.onmousemove = null;
        document.onmouseup = null;
        document._isDialogResizing = false;
      },
    },
  },
  props: {
    visible: { type: Boolean, default: false },
    engineeringOrderId: { type: [String, Number], default: "" },
    type: { type: String, default: "NORMAL", validator: (val) => ["NORMAL", "SUPPLEMENTARY"].includes(val) },
  },
  data() {
    return { loading: false, formData: {} };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
    dialogTitle() { return this.type === "SUPPLEMENTARY" ? "查看增补收方表" : "查看收方表"; },
    hasData() { return !!(this.formData.projectName || (this.formData.stages && this.formData.stages.length)); },
    stages() { return this.formData.stages || []; },
    extraMapEntries() {
      if (!this.formData.extra || !this.formData.extra.extraMap) return [];
      return Object.keys(this.formData.extra.extraMap)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => ({ key, value: this.formData.extra.extraMap[key] }));
    },
  },
  methods: {
    handleOpen() {
      this.fetchData();
      this.$nextTick(() => {
        const dialog = document.querySelector('.measurement-form-dialog');
        if (dialog) {
          const wrapper = dialog.parentElement;
          if (wrapper && wrapper.classList.contains('el-dialog__wrapper')) {
            wrapper.style.display = 'flex'; wrapper.style.alignItems = 'center'; wrapper.style.justifyContent = 'center';
          }
          dialog.style.position = ''; dialog.style.left = ''; dialog.style.top = '';
          dialog.style.margin = '0'; dialog.style.width = ''; dialog.style.height = ''; dialog.style.cursor = '';
        }
      });
    },
    handleClosed() {
      this.formData = {};
      // 关闭后清理可能残留的拖拽/缩放状态，避免下次打开时残留事件影响交互
      document.onmousemove = null;
      document.onmouseup = null;
      document._isDialogResizing = false;
    },
    handleClose() {
      // 关闭前清理可能残留的拖拽/缩放状态，防止残留事件拦截 click
      document.onmousemove = null;
      document.onmouseup = null;
      document._isDialogResizing = false;
      this.dialogVisible = false;
    },
    async fetchData() {
      if (!this.engineeringOrderId) return;
      this.formData = {};
      try {
        this.loading = true;
        const res = await getSquareVolume(this.engineeringOrderId, this.type);
        if (res && res.code && res.code !== "200") {
          this.$message.error(res.message || "获取收方表数据失败"); return;
        }
        if (res && res.code === "200" && res.data) {
          this.formData = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        } else {
          this.formData = res || {};
        }
      } catch (error) {
        console.error("获取收方表数据失败:", error);
        this.$message.error("获取收方表数据失败");
      } finally { this.loading = false; }
    },
    stageCountRows(stage) {
      let n = 0;
      if (stage.pitCount != null) n++;
      if (stage.secondRowCount != null) n++;
      if (stage.moldCount != null) n++;
      if (stage.floorCount != null) n++;
      if (stage.lossOfEarnings != null) n++;
      return n;
    },
    getStageRows(stage) {
      const rows = [];
      const items = (stage.items && stage.items.length) ? stage.items : [{}];
      items.forEach((item, idx) => {
        rows.push({ ...item, _type: 'item', _first: idx === 0 });
      });
      if (stage.pitCount != null) rows.push({ _type: 'count', _label: '沉箱数量（个）：' + stage.pitCount, _first: false });
      if (stage.secondRowCount != null) rows.push({ _type: 'count', _label: '二排数量（个）：' + stage.secondRowCount, _first: false });
      if (stage.moldCount != null) rows.push({ _type: 'count', _label: '模具数量（个）：' + stage.moldCount, _first: false });
      if (stage.floorCount != null) rows.push({ _type: 'count', _label: '楼层（步梯）：' + stage.floorCount + ' 层', _first: false });
      if (stage.lossOfEarnings != null) rows.push({ _type: 'count', _label: '误工费（元）：' + stage.lossOfEarnings, _first: false });
      return rows;
    },
    stageRowSpan(stage) {
      const itemCount = (stage.items && stage.items.length) ? stage.items.length : 1;
      return itemCount + this.stageCountRows(stage);
    },
    formatNum(val) { return val != null ? val : ''; },
    formatDate(ts) {
      if (!ts) return '--';
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '--';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    handlePrint() {
      const el = this.$refs.printArea;
      if (!el) return;
      const printWin = window.open('', '_blank');
      if (!printWin) { this.$message.warning('请允许弹出窗口以使用打印功能'); return; }
      printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>收方表打印</title><style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: SimSun, "宋体", serif; font-size: 13px; color: #000; padding: 20px; }
        ${this.getPrintStyles()}
      </style></head><body>${el.innerHTML}</body></html>`);
      printWin.document.close();
      printWin.focus();
      setTimeout(() => { printWin.print(); printWin.close(); }, 300);
    },
    getPrintStyles() {
      return `
        .form-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 0 4px; }
        .form-header-left, .form-header-right { font-size: 14px; }
        .header-label { font-weight: bold; }
        .header-value { border-bottom: 1px solid #000; min-width: 160px; display: inline-block; padding: 0 4px; }
        .mf-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .mf-table th, .mf-table td { border: 1px solid #000; padding: 6px 4px; text-align: center; vertical-align: middle; word-break: break-all; font-size: 13px; }
        .mf-table thead th { background: #f0f0f0; font-weight: bold; }
        .col-stage { width: 9%; } .col-part { width: 11%; } .col-perimeter { width: 10%; } .col-height { width: 9%; }
        .col-area { width: 11%; } .col-expr { width: 16%; } .col-plane { width: 12%; } .col-worker { width: 13%; }
        .col-group { font-weight: bold; }
        .td-stage { font-weight: bold; writing-mode: horizontal-tb; text-align: center; }
        .td-num { text-align: center; } .td-expr { text-align: center; font-size: 12px; }
        .td-part { text-align: center; } .td-worker { text-align: center; font-weight: bold; }
        .td-count-row { text-align: left; padding-left: 20px; font-size: 13px; }
        .td-extra-desc { text-align: left; padding-left: 10px; }
        .td-summary-label { font-weight: bold; }
        .td-summary-cell { text-align: center; }
        .td-summary-total { font-weight: bold; }
        .summary-tag { font-size: 12px; }
        .tr-summary td { background: #f9f9f9; }
        .sign-section { margin-top: 30px; page-break-inside: avoid; }
        .sign-title { font-weight: bold; font-size: 14px; margin-bottom: 12px; }
        .sign-body { display: flex; align-items: flex-end; gap: 30px; }
        .sign-image-area { border: 1px solid #000; padding: 4px; display: inline-block; }
        .sign-photo { max-width: 200px; max-height: 100px; display: block; }
        .sign-info-area { font-size: 13px; }
        .sign-info-row { margin-bottom: 6px; }
        .sign-info-label { font-weight: bold; }
        .sign-line { border-bottom: 1px solid #000; width: 300px; margin-top: 40px; }
        .notes-section { margin-top: 30px; font-size: 12px; page-break-inside: avoid; }
        .notes-title { font-weight: bold; margin-bottom: 6px; }
        .notes-item { margin-bottom: 4px; line-height: 1.6; }
        .notes-indent { padding-left: 20px; line-height: 1.6; }
        @media print { body { padding: 10mm; } .no-print { display: none !important; } }
      `;
    },
  },
};
</script>

<style lang="scss" scoped>
.measurement-wrapper {
  padding: 0 4px;
}

.print-area {
  font-family: SimSun, "宋体", serif;
  color: #303133;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 4px;

  .form-header-left,
  .form-header-right {
    font-size: 14px;
  }

  .header-label {
    font-weight: 600;
    color: #303133;
  }

  .header-value {
    border-bottom: 1px solid #606266;
    min-width: 160px;
    display: inline-block;
    padding: 0 6px;
    color: #303133;
  }
}

/* ---- 主表格 ---- */
.mf-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th, td {
    border: 1px solid #909399;
    padding: 7px 5px;
    text-align: center;
    vertical-align: middle;
    word-break: break-all;
    font-size: 13px;
    line-height: 1.5;
  }

  thead th {
    background: #f5f7fa;
    font-weight: 600;
    color: #303133;
  }

  .col-stage { width: 9%; }
  .col-part { width: 11%; }
  .col-perimeter { width: 10%; }
  .col-height { width: 9%; }
  .col-area { width: 11%; }
  .col-expr { width: 16%; }
  .col-plane { width: 12%; }
  .col-worker { width: 13%; }
}

.td-stage {
  font-weight: 600;
  color: #303133;
  background: #fafafa;
}

.td-num { text-align: center; }
.td-expr { text-align: center; font-size: 12px; color: #606266; }
.td-part { text-align: center; }
.td-worker { text-align: center; font-weight: 600; background: #fafafa; }

.td-count-row {
  text-align: left;
  padding-left: 24px;
  color: #606266;
  font-size: 13px;
}

.td-extra-desc {
  text-align: left;
  padding-left: 12px;
  color: #303133;
}

/* 汇总行 */
.tr-summary td {
  background: #f5f7fa;
  font-size: 13px;
}

.td-summary-label {
  font-weight: 600;
  color: #303133;
}

.td-summary-cell {
  text-align: center;
}

.td-summary-total {
  font-weight: 700;
  color: #303133;
}

.summary-tag {
  font-size: 12px;
  color: #909399;
}

/* ---- 签名区域 ---- */
.sign-section {
  margin-top: 32px;
  page-break-inside: avoid;
}

.sign-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 14px;
  color: #303133;
}

.sign-body {
  display: flex;
  align-items: flex-end;
  gap: 36px;
}

.sign-image-area {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 6px;
  display: inline-block;
  background: #fafafa;
}

.sign-photo {
  max-width: 220px;
  max-height: 120px;
  display: block;
}

.sign-info-area {
  font-size: 13px;
  color: #606266;
}

.sign-info-row {
  margin-bottom: 8px;
}

.sign-info-label {
  font-weight: 600;
  color: #303133;
}

.sign-line {
  border-bottom: 1px solid #909399;
  width: 320px;
  margin-top: 48px;
}

/* ---- 说明区域 ---- */
.notes-section {
  margin-top: 32px;
  font-size: 12px;
  color: #606266;
  page-break-inside: avoid;
}

.notes-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #303133;
}

.notes-item {
  margin-bottom: 4px;
  line-height: 1.8;
}

.notes-indent {
  padding-left: 22px;
  line-height: 1.8;
}

.no-data-block {
  text-align: center;
  padding: 60px 0;
  color: #c0c4cc;
  p { margin-top: 12px; font-size: 14px; }
}
</style>

<style>
.measurement-form-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.measurement-form-dialog .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
